import { useCallback, useEffect, useState } from 'react';
import { useCart } from './useCart';
import { setCartCode } from '../../../utils/cartStorage';
import type { 
  FormData, 
  PaymentInformationData, 
  ShippingAddressData, 
  UserIdentificationData 
} from '../../../types';
import type { ShopifyOfferDto } from '../../../offer.types';
import { convertBirthDateToISO, convertISOToBirthDate } from '../../../utils/date-converters';
import { getOfferSubtotal, getOfferTotal, getOfferDiscount } from '../../../utils/pricing';
import { cpfOnIdentificationFromTheme, requestBirthDateFromTheme } from '../../../utils/checkoutTheme';

export const useCheckoutForm = (shopifyOffer: ShopifyOfferDto) => {
  // ✅ Adicionar useCart hook (sem modificar lógica existente)
  const cartHook = useCart();

  const DefaultFormData = useCallback(() => ({
    userIdentification: {
      fullName: '',
      email: '',
      cpf: '',
      phone: '',
      birth: '',
      cpfOnIdentification: cpfOnIdentificationFromTheme(shopifyOffer),
      requestBirthDate: requestBirthDateFromTheme(shopifyOffer),
    } as UserIdentificationData,

    shippingAddress: { 
      zipCode: '', 
      street: '', 
      number: '', 
      neighborhood: '', 
      city: '', 
      state: '', 
      complement: '' 
    } as ShippingAddressData,
    
    paymentInformation: {
      paymentMethod: '',
      creditCard: { 
        cardNumber: '', 
        expirationDate: '', 
        cvv: '', 
        cardholderName: '', 
        cpf: '', 
        installments: '' 
      },
      pix: { cpf: '' },
      boleto: { cpf: '' },
    } as PaymentInformationData,

    orderSummary: {
      products: [],
      subtotal: 0,
      shipping: 0,
      total: 0,
      couponCode: '',
      coupons: [],
      discountAmount: 0,
    },
    shopifyOffer: shopifyOffer,
  } as FormData), [shopifyOffer]);

  // Função para garantir que os dados da oferta estão atualizados
  const ensureOfferDataIsCurrent = useCallback((data: FormData) => {
    if (data.shopifyOffer.uuid !== shopifyOffer.uuid) {
      return DefaultFormData();
    }
    data.shopifyOffer = shopifyOffer; // Ensure offer data is current
    return data;
  }, [shopifyOffer, DefaultFormData]);

  const [formData, setFormData] = useState<FormData>(DefaultFormData());
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [cartLoading, setCartLoading] = useState(true); // ✅ Inicia como true para aguardar verificação do carrinho

  // ✅ Função para buscar dados atualizados do carrinho no backend
  const fetchCartData = useCallback(async (cartCode: string) => {
    try {
      
      const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/carts/${cartCode}/recover`);
      
      if (!response.ok) {
        throw new Error(`Erro ao buscar carrinho: ${response.statusText}`);
      }
      
      const cartData = await response.json();
      
      return cartData;
    } catch (error) {
      console.error('❌ useCheckoutForm - Erro ao buscar dados do carrinho:', error);
      return null;
    }
  }, []);

  // ✅ Carrega carrinho existente do localStorage com validação de oferta
  useEffect(() => {
    if (shopifyOffer?.nanoId) {
      
      // ✅ Passo 1: Verifica se é recuperação de carrinho pela URL
      const pathSegments = window.location.pathname.split('/').filter(p => p !== '');
      const isCartRecovery = pathSegments.length >= 2 && pathSegments[0] === 'cart';
      
      if (isCartRecovery) {
        
        // ✅ Se for carrinho recuperado pela URL, os dados já vêm prontos do backend
        // ✅ Apenas salva o cartCode no localStorage para futuras operações
        if ((shopifyOffer as any).cartCode) {
          try {
            setCartCode((shopifyOffer as any).cartCode);
          } catch (error) {
            console.error('❌ useCheckoutForm - Erro ao salvar cartCode:', error);
          }
        }
        
        // ✅ IMPORTANTE: Popula o formulário com os dados que já vêm no shopifyOffer
        if ((shopifyOffer as any).customerData) {
          setFormData(prev => {
            const updatedData = {
              ...prev,
              userIdentification: {
                fullName: (shopifyOffer as any).customerData.name || '',
                email: (shopifyOffer as any).customerData.email || '',
                phone: (shopifyOffer as any).customerData.phone || '',
                cpf: (shopifyOffer as any).customerData.document || '',
                birth: (shopifyOffer as any).customerData.birth ? convertISOToBirthDate((shopifyOffer as any).customerData.birth) : '',
                cpfOnIdentification: cpfOnIdentificationFromTheme(shopifyOffer),
                requestBirthDate: requestBirthDateFromTheme(shopifyOffer),
              },
              shippingAddress: {
                zipCode: (shopifyOffer as any).customerData.address?.postcode || '',
                street: (shopifyOffer as any).customerData.address?.street || '',
                number: (shopifyOffer as any).customerData.address?.streetNumber || '',
                neighborhood: (shopifyOffer as any).customerData.address?.streetDistrict || '',
                city: (shopifyOffer as any).customerData.address?.city || '',
                state: (shopifyOffer as any).customerData.address?.state || '',
                complement: (shopifyOffer as any).customerData.address?.streetComplement || '',
              },
              orderSummary: {
                ...prev.orderSummary,
                // ✅ SEMPRE usa produtos da OFERTA (matriz) - nunca do carrinho
                products: (shopifyOffer.items ?? [])
                  .filter(i => i.source === 'offer')
                  .map(item => ({
                    id: String(item.variant_id || 'unknown'),
                    name: item.name || 'Produto sem nome',
                    price: Number(item.price),
                    quantity: item.quantity,
                    totalPrice: Number(item.price) * item.quantity,
                    imageUrl: item.imageUrl ?? '',
                  })),
                // ✅ SEMPRE usa preços da OFERTA (matriz) - nunca do carrinho
                subtotal: getOfferSubtotal(shopifyOffer),
                shipping: Number(shopifyOffer.shipping_price),
                total: getOfferTotal(shopifyOffer),
                discountAmount: getOfferDiscount(shopifyOffer),
              },
            };

            return updatedData;
          });

          // ✅ Define o currentStep do carrinho recuperado
          if ((shopifyOffer as any).currentStep) {
            setCurrentStep((shopifyOffer as any).currentStep);
          }
        }
        
        // ✅ Remove loading do carrinho
        setCartLoading(false);
        return;
      }
      
      // ✅ Passo 2: Se não for recuperação pela URL, verifica localStorage
      cartHook.loadExistingCart(shopifyOffer.nanoId)
        .then(async (cartData) => {
          if (cartData) {
            
            // ✅ IMPORTANTE: Mesmo com carrinho no localStorage, os dados do cliente
            // ✅ devem vir do backend, não do localStorage
            
            try {
              const updatedCartData = await fetchCartData(cartData.cartCode);
              
              if (updatedCartData && updatedCartData.customerData) {
                
                // ✅ Carrega dados do cliente no formulário
                setFormData(prev => {
                  const updatedData = {
                    ...prev,
                    userIdentification: {
                      fullName: updatedCartData.customerData.name || '',
                      email: updatedCartData.customerData.email || '',
                      phone: updatedCartData.customerData.phone || '',
                      cpf: updatedCartData.customerData.document || '',
                      birth: updatedCartData.customerData.birth ? convertISOToBirthDate(updatedCartData.customerData.birth) : '',
                      cpfOnIdentification: cpfOnIdentificationFromTheme(shopifyOffer),
                      requestBirthDate: requestBirthDateFromTheme(shopifyOffer),
                    },
                    shippingAddress: {
                      zipCode: updatedCartData.customerData.address?.postcode || '',
                      street: updatedCartData.customerData.address?.street || '',
                      number: updatedCartData.customerData.address?.streetNumber || '',
                      neighborhood: updatedCartData.customerData.address?.streetDistrict || '',
                      city: updatedCartData.customerData.address?.city || '',
                      state: updatedCartData.customerData.address?.state || '',
                      complement: updatedCartData.customerData.address?.streetComplement || '',
                    },
                    orderSummary: {
                      ...prev.orderSummary,
                      // ✅ SEMPRE usa produtos da OFERTA (matriz) - nunca do carrinho
                      products: (shopifyOffer.items ?? [])
                        .filter(i => i.source === 'offer')
                        .map(item => ({
                          id: String(item.variant_id || 'unknown'),
                          name: item.name || 'Produto sem nome',
                          price: Number(item.price),
                          quantity: item.quantity,
                          totalPrice: Number(item.price) * item.quantity,
                          imageUrl: item.imageUrl ?? '',
                        })),
                      // ✅ SEMPRE usa preços da OFERTA (matriz) - nunca do carrinho
                      subtotal: getOfferSubtotal(shopifyOffer),
                      shipping: Number(shopifyOffer.shipping_price),
                      total: getOfferTotal(shopifyOffer),
                      discountAmount: getOfferDiscount(shopifyOffer),
                    },
                  };
                  
                  return updatedData;
                });
                
                // ✅ Define o currentStep do carrinho se existir
                if (updatedCartData.currentStep) {
                  setCurrentStep(updatedCartData.currentStep);
                }
              }
            } catch {
              // cart fetch é best-effort; erros não bloqueiam o checkout
            }
          }
        })
        .catch(console.error)
        .finally(() => {
          // ✅ Sempre remove loading do carrinho após verificação
          setCartLoading(false);
        });
    } else {
      // ✅ Se não tem shopifyOffer, remove loading do carrinho
      setCartLoading(false);
    }
  }, [shopifyOffer?.nanoId, setFormData, setCurrentStep]);

  const updateOrderSummary = useCallback(() => {
    // Check if there's an auto-applied coupon
    const autoAppliedCoupon = shopifyOffer.autoAppliedCoupon;
    
    // ✅ Migrado para camelCase (dados unificados)
    const subtotal = getOfferSubtotal(shopifyOffer);
    const total = getOfferTotal(shopifyOffer);
    const discountAmount = getOfferDiscount(shopifyOffer) || (autoAppliedCoupon ? (shopifyOffer.original_price - shopifyOffer.price) : 0);
    
    setFormData(prev => Object.assign(DefaultFormData(), prev, {
      orderSummary: {
        ...prev.orderSummary,
        products: (shopifyOffer.items ?? [])
          .filter(i => i.source === 'offer')
          .map(item => ({
            id: String(item.variant_id || 'unknown'),
            name: item.name || 'Produto sem nome',
            price: Number(item.price),
            quantity: item.quantity,
            totalPrice: Number(item.price) * item.quantity,
            imageUrl: item.imageUrl ?? '',
          })),
        subtotal,
        shipping: Number(shopifyOffer.shipping_price),
        total,
        discountAmount,
        // ✅ Preservar cupons manuais se já existirem, ou usar auto-applied coupon
        ...(prev.orderSummary?.coupons && prev.orderSummary.coupons.length > 0
          ? {
              // Preservar cupons manuais já aplicados
              couponCode: prev.orderSummary.couponCode,
              coupons: prev.orderSummary.coupons,
            }
          : autoAppliedCoupon
          ? {
              // Usar auto-applied coupon se não houver cupons manuais
              couponCode: autoAppliedCoupon.code,
              coupons: [autoAppliedCoupon],
            }
          : {
              // Limpar cupons se não houver nenhum
              couponCode: '',
              coupons: [],
            }),
      },
      shopifyOffer: shopifyOffer,
    }));
  }, [shopifyOffer, setFormData, DefaultFormData]);

  const updateFormData = useCallback((updatedData: Partial<FormData>) => {
    setFormData(prev => ({ ...prev, ...updatedData }));
  }, [setFormData]);

  // ✅ Função para criar/atualizar cart — aceita dados parciais/vazios (presença/ao vivo).
  // O backend só dispara recuperação quando há email/telefone; aqui é só captura.
  const createOrUpdateCartData = useCallback(async (userData: any) => {
    try {
      const cartData = {
        storeId: shopifyOffer.store?.nanoId ?? shopifyOffer.storeId,
        offerId: shopifyOffer.id > 0 ? shopifyOffer.id : undefined, // ✅ Só envia offerId se for uma oferta real
        offerNanoId: shopifyOffer.nanoId, // ✅ Sempre envia offerNanoId
        customerData: {
          name: userData.fullName || '',
          email: userData.email || '',
          phone: userData.phone || '',
          document: userData.document || undefined,
          birth: userData.birth ? convertBirthDateToISO(userData.birth) : undefined,
          address: {
            postcode: userData.address?.postcode || '',
            street: userData.address?.street || '',
            streetNumber: userData.address?.streetNumber || '',
            streetComplement: userData.address?.streetComplement || '',
            streetDistrict: userData.address?.streetDistrict || '',
            city: userData.address?.city || '',
            state: userData.address?.state || '',
          },
        },
        items: (shopifyOffer.items ?? [])
          .filter(i => i.source === 'offer')
          .map(item => ({
            productId: Number(item.variant_id) || 0,
            quantity: item.quantity,
            price: Number(item.price),
            name: item.name || 'Produto sem nome',
            image: item.imageUrl || '',
          })),
        totalPrice: formData.orderSummary.total,
        totalDiscounts: formData.orderSummary.discountAmount,
        appliedCoupons: formData.orderSummary.coupons?.map(coupon => ({
          code: coupon.code,
          discount: coupon.discountValue,
          type: coupon.discountType,
        })),
        // ✅ Prioriza o currentStep fornecido pelo userData (próxima etapa)
        currentStep: userData.currentStep || currentStep,
      };
      
      // ✅ CHAMA O HOOK PARA CRIAR/ATUALIZAR O CARRINHO
      const result = await cartHook.createOrUpdateCart(cartData);
      return result;
      
    } catch (error) {
      throw error;
    }
  }, [shopifyOffer, formData.orderSummary, cartHook, currentStep]);

  return {
    formData,
    setFormData,
    updateOrderSummary,
    updateFormData,
    cartHook, // ✅ Adicionar cartHook no retorno
    createOrUpdateCartData, // ✅ Nova função para criar cart
    currentStep, // ✅ Retorna o currentStep do estado
    cartLoading, // ✅ Retorna o estado de loading do carrinho
    concludeCart: cartHook.concludeCart, // ✅ Exporta função para concluir carrinho
  };
}; 